<!-- eslint-disable vue/no-v-html -->
<template>
  <div class="contact-us">
    <div
      v-if="displayFormSuccess"
      ref="formsuccess"
      class="contact-us__intro-banner contact-us__intro-banner--border-bottom"
    >
      <slot name="form-success" />
    </div>
    <div v-if="!displayFormSuccess">
      <div class="contact-us__intro-banner">
        <slot name="intro-banner" />
      </div>
      <div class="contact-us__intro-banner topics">
        <slot name="topics-banner" />

        <div class="topics-input">
          <div class="topics-input__required-text">
            {{ productData.requiredText }}
          </div>
          <div class="form-group form-group--select">
            <label>
              <span class="is-sr-only">{{ selectTopicLabel }}</span>
              <select
                id="topic"
                v-model="selectedTopic"
                class="input-text"
                name="topic"
              >
                <option value="">{{ selectTopicLabel }}</option>
                <optgroup
                  v-for="(group) in optionGroups"
                  :key="`group_${group.label}`"
                  :label="group.label"
                >
                  <option
                    v-for="(topic) in group.topics"
                    :key="`key_${topic.label}`"
                    :value="topic"
                    v-text="topic.label"
                  />
                </optgroup>
              </select>
            </label>
          </div>
        </div>

        <div
          v-show="selectedTopic !== ''"
          class="contact-us__faq"
        >
          <template v-if="selectedFaq.length">
            <div class="intro-copy__title">
              <h2>{{ faq.title }}</h2>
            </div>
            <ul>
              <li
                v-for="(faqItem, i) in selectedFaq"
                :key="`faq-item_${i}`"
              >
                <accordion
                  :title="faqItem.title"
                  :expand-label="faq.expandLabel"
                  :collapse-label="faq.collapseLabel"
                  :event-category="faq.eventCategory"
                  :event-detail="faqItem.title"
                  :event-label="faq.eventLabel"
                  event-name="faq"
                  :event-action="faq.eventAction"
                >
                  <template slot="heading">
                    <h2>{{ faqItem.title }}</h2>
                  </template>
                  <div v-html="faqItem.content" />
                </accordion>
              </li>
            </ul>
          </template>
          <slot name="topics-link" />
        </div>
      </div>

      <two-columns
        v-show="selectedTopic"
        :sticky="true"
      >
        <form
          ref="form"
          class="contact-us__form"
        >
          <!-- Intro Section -->
          <div class="contact-us__form__section contact-us__intro-banner">
            <slot name="intro-text" />
          </div>

          <!-- Product Section -->
          <div
            v-if="productSection"
            class="contact-us__form__section"
          >
            <div class="contact-us__form__section__intro">
              <div class="contact-us__form__section__intro-title">
                {{ productData.title }}
              </div>
              <div class="contact-us__form__section__intro-required-text">
                {{ productData.requiredText }}
              </div>
            </div>

            <div class="form-group form-group--select">
              <label>
                <span class="is-sr-only">{{ productData.inputName }}</span>
                <select
                  id="product"
                  ref="selectProduct"
                  v-model="selectedProduct"
                  class="input-text"
                  name="product"
                >
                  <option value="">{{ productData.inputName }}</option>
                  <option
                    v-for="(option) in productData.products"
                    :key="`key_${option.id}`"
                    :value="option"
                    v-text="option.name"
                  />
                </select>
              </label>
              <div
                id="selected-product-error"
                class="errors-log errors-log--show"
              >
                <span
                  v-if="selectProductError"
                  class="message error message--show"
                  v-text="productRequiredErrorMessage"
                />
              </div>
            </div>
            <div
              v-if="selectedProductHasSizes"
              class="form-group form-group--select"
            >
              <label>
                <span class="is-sr-only">{{ productData.inputSize }}</span>
                <select
                  id="product-size"
                  v-model="selectedProductSize"
                  class="input-text"
                  name="product-size"
                >
                  <option value="">{{ productData.inputSize }}</option>
                  <option
                    v-for="(option) in selectedProductSizes"
                    :key="`key_${option.size}`"
                    :value="option"
                    v-text="option.size"
                  />
                </select>
              </label>
            </div>
            <div class="form-group">
              <label>
                <span class="is-sr-only">{{ productData.inputCode }}</span>
                <input
                  id="product-purchase-info"
                  v-model="selectedProductCode"
                  type="text"
                  name="product-purchase-info"
                  class="input-text"
                  :placeholder="productData.inputCode"
                >
              </label>
              <button
                v-if="productData.tooltipCode"
                class="tooltip-trigger"
                @mouseout="hideCodeTooltip"
                @mouseover="showCodeTooltip"
              >
                ?
                <div
                  v-show="displayCodeTooltip"
                  class="tooltip"
                  v-html="productData.tooltipCode"
                />
              </button>
            </div>
            <div class="form-group">
              <label>
                <span class="is-sr-only">{{ productData.inputPurchase }}</span>
                <input
                  id="product-purchase-info"
                  v-model="selectedProductPurchase"
                  type="text"
                  name="product-purchase-info"
                  class="input-text"
                  :placeholder="productData.inputPurchase"
                >
              </label>
            </div>
          </div>

          <!-- Personal Section -->
          <div
            v-if="personalSection"
            class="contact-us__form__section"
          >
            <div class="contact-us__form__section__intro">
              <div class="contact-us__form__section__intro-title">
                {{ personalDataTitle }}
              </div>
              <div class="contact-us__form__section__intro-required-text">
                {{ requiredFieldText }}
              </div>
            </div>

            <div class="form-group">
              <label>
                <span class="is-sr-only">{{ firstNamePlaceholder }}</span>
                <input
                  id="contact-first-name"
                  ref="firstName"
                  v-model="firstName"
                  type="text"
                  name="firstName"
                  class="input-text"
                  :placeholder="firstNamePlaceholder"
                  :required="firstNameRequired"
                  aria-describedby="first-name-error"
                  :aria-invalid="firstNameError ? 'true' : 'false'"
                  @input="dirtyFirstName"
                >
                <div
                  id="first-name-error"
                  class="errors-log errors-log--show"
                >
                  <span
                    v-if="firstNameError"
                    class="message error message--show"
                    v-text="firstNameRequiredErrorMessage"
                  />
                </div>
              </label>
            </div>

            <div class="form-group">
              <label>
                <span class="is-sr-only">{{ lastNamePlaceholder }}</span>
                <input
                  id="contact-last-name"
                  ref="lastName"
                  v-model="lastName"
                  type="text"
                  name="lastName"
                  class="input-text"
                  :placeholder="lastNamePlaceholder"
                  :required="lastNameRequired"
                  aria-describedby="last-name-error"
                  :aria-invalid="lastNameError ? 'true' : 'false'"
                  @input="dirtyLastName"
                >
                <div
                  id="last-name-error"
                  class="errors-log errors-log--show"
                >
                  <span
                    v-if="lastNameError"
                    class="message error message--show"
                    v-text="lastNameRequiredErrorMessage"
                  />
                </div>
              </label>
            </div>

            <div class="form-group">
              <label>
                <span class="is-sr-only">{{ emailPlaceholder }}</span>
                <input
                  id="email"
                  ref="emailField"
                  v-model="emailAddress"
                  type="text"
                  name="email"
                  class="input-text"
                  aria-describedby="email-errors"
                  :aria-invalid="(invalidError || requiredError) ? 'true' : 'false'"
                  :placeholder="emailPlaceholder"
                  @change="dirtyEmail"
                >
              </label>
              <div
                id="email-errors"
                aria-live="polite"
                :class="['errors-log', { 'errors-log--show' : requiredError || invalidError}]"
              >
                <div :class="['message error', {'message--show': requiredError}]">
                  <span v-if="requiredError">{{ emailRequiredErrorMessage }}</span>
                </div>
                <div :class="['message error', {'message--show': !requiredError && invalidError}]">
                  <span v-if="!requiredError && invalidError">{{ emailFormatErrorMessage }}</span>
                </div>
                <div :class="['message error', {'message--show': !(requiredError && invalidError) && serverError}]">
                  <span v-if="!(requiredError && invalidError) && serverError">{{ serverErrorMessage }}</span>
                </div>
              </div>
            </div>

            <div class="form-group">
              <label>
                <span class="is-sr-only">{{ confirmEmailPlaceholder }}</span>
                <input
                  id="confirmEmail"
                  ref="confirmEmail"
                  v-model="confirmEmailAddress"
                  type="text"
                  name="confirm email"
                  class="input-text"
                  aria-describedby="confirm-email-error"
                  :aria-invalid="confirmEmailError ? 'true' : 'false'"
                  :placeholder="confirmEmailPlaceholder"
                  @change="dirtyConfirmEmail"
                >
              </label>
              <div
                id="confirm-email-error"
                class="errors-log errors-log--show"
              >
                <span
                  v-if="confirmEmailError"
                  class="message error message--show"
                  v-text="confirmEmailErrorMessage"
                />
              </div>
            </div>
          </div>

          <!-- Location Section -->
          <div
            v-if="locationSection"
            class="contact-us__form__address contact-us__form__section"
          >
            <button
              :class="['contact-us__form__address--trigger contact-us__form__section__intro-title', { 'contact-us__form__address--trigger-opened' : displayingAddress}]"
              aria-controls="address-content"
              :aria-expanded="displayingAddress"
              @click.prevent="toggleAddressDisplay()"
            >
              {{ locationTitle }}
            </button>
            <div class="contact-us__form__address--description">
              {{ locationDescription }}
            </div>
            <div
              v-show="displayingAddress"
              id="address-content"
              class="contact-us__form__address--content"
            >
              <div class="form-group">
                <label>
                  <span class="is-sr-only">{{ streetPlaceholder }}</span>
                  <input
                    id="contact-street"
                    ref="street"
                    v-model="street"
                    type="text"
                    name="street"
                    class="input-text"
                    :placeholder="streetPlaceholder"
                  >
                </label>
              </div>

              <div class="form-group">
                <label>
                  <span class="is-sr-only">{{ street2Placeholder }}</span>
                  <input
                    id="contact-street2"
                    ref="street2"
                    v-model="street2"
                    type="text"
                    name="street2"
                    class="input-text"
                    :placeholder="street2Placeholder"
                  >
                </label>
              </div>

              <div class="location-multiple">
                <div class="form-group">
                  <label>
                    <span class="is-sr-only">{{ cityPlaceholder }}</span>
                    <input
                      id="contact-city"
                      ref="city"
                      v-model="city"
                      type="text"
                      name="city"
                      class="input-text"
                      :placeholder="cityPlaceholder"
                    >
                  </label>
                </div>

                <div class="form-group form-group--select">
                  <label>
                    <span class="is-sr-only">{{ statePlaceholder }}</span>
                    <select
                      id="contact-state"
                      v-model="state"
                      class="input-text"
                      name="state"
                    >
                      <option value="">{{ statePlaceholder }}</option>
                      <option
                        v-for="(option) in stateSelectList"
                        :key="`key_${option}`"
                        :value="option"
                      >{{ option }}</option>
                    </select>
                  </label>
                </div>

                <div class="form-group">
                  <label>
                    <span class="is-sr-only">{{ zipcodePlaceholder }}</span>
                    <input
                      id="contact-zip-code"
                      ref="zipcode"
                      v-model="zipcode"
                      autocomplete="postal-code"
                      type="text"
                      name="zip-code"
                      class="input-text"
                      :pattern="zipCodePattern"
                      :placeholder="zipcodePlaceholder"
                      @change="dirtyZipcode"
                      @keypress="validateZipCodeKeypress($event)"
                    >
                  </label>
                  <div
                    id="zipcode-error"
                    class="errors-log errors-log--show"
                  >
                    <span
                      v-if="zipcodeError"
                      class="message error message--show"
                      v-text="zipcodeErrorMessage"
                    />
                  </div>
                </div>
              </div>

              <div class="form-group">
                <label>
                  <span class="is-sr-only">{{ phonePlaceholder }}</span>
                  <input
                    id="contact-phone"
                    ref="phone"
                    v-model="phone"
                    type="text"
                    name="phone"
                    class="input-text"
                    :placeholder="phonePlaceholder"
                    @change="dirtyPhone"
                    @keypress="isNumber($event)"
                  >
                </label>
                <div
                  id="phone-error"
                  class="errors-log errors-log--show"
                >
                  <span
                    v-if="phoneError"
                    class="message error message--show"
                    v-text="phoneErrorMessage"
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- Message Section -->
          <div
            v-if="messageSection"
            class="contact-us__form__section"
          >
            <div class="contact-us__form__section__intro-title">
              {{ messageTitle }}
            </div>
            <div class="form-group">
              <textarea
                ref="message"
                v-model="message"
                aria-describedby="message-error"
                :aria-invalid="messageError ? 'true' : 'false'"
                :maxlength="messageCharacterCount ? messageCharacterCount:''"
                name="message"
                :placeholder="messagePlaceholder"
                @input="dirtyMessage"
              />
              <div
                v-if="messageCharacterCount"
                class="character-count"
              >
                {{ message.length }}/{{ messageCharacterCount }}
              </div>
              <div
                id="message-error"
                class="errors-log errors-log--show"
              >
                <span
                  v-if="messageError"
                  class="message error message--show"
                  v-text="messageErrorMessage"
                />
              </div>
            </div>
          </div>

          <div class="contact-us__form__bottom">
            <div class="contact-us__form__legal-copy">
              <div
                v-if="displayDisclaimer" 
                class="form-group form-group--checkbox"
              >
                <label>
                  <input
                    id="disclaimer"
                    ref="disclaimer"
                    v-model="disclaimerChecked"
                    type="checkbox"
                    name="disclaimer"
                    required
                    :aria-invalid="disclaimerError ? 'true' : 'false'"
                    aria-describedby="disclaimer-error"
                  >
                  <span
                    for="disclaimer"
                    v-html="disclaimerText"
                  />
                </label>
                <div
                  id="disclaimer-error"
                  class="errors-log errors-log--show"
                >
                  <span
                    v-if="displayDisclaimer && disclaimerError && !disclaimerChecked"
                    class="message error message--show"
                    v-text="disclaimerErrorMessage"
                  />
                </div>
              </div>
              <div
                v-if="recaptcha"
                class="recaptcha"
              >
                <slot name="recaptcha-branding" />
                <div
                  v-if="recaptchaError"
                  class="recaptcha__error field-validation-error"
                >
                  <slot name="recaptcha-error" />
                </div>
              </div>
            </div>
            <button
              class="btn btn--secondary"
              @click.prevent="submit"
            >
              {{ submitBtnLabel }}
            </button>
          </div>
        </form>
        <template slot="right">
          <slot name="right" />
        </template>
      </two-columns>
    </div>
  </div>
</template>

<script>
  import { AnalyticsHandler } from '@Foundation/analyticsHandler';
  import { tag } from '@Foundation/analyticsHandler/directives/tag';
  import { recaptcha } from '@Foundation/mixins/recaptcha';
  import regex from '@Foundation/utilities/regex';
  import { smoothScroll } from '@Foundation/utilities/scroll/smoothScroll';
  import TwoColumns from '@Feature/TwoColumns/code/Scripts/components/TwoColumns/TwoColumns.vue';
  import Accordion from '@Feature/Accordion/code/Scripts/components/Accordion/Accordion.vue';

  export default {
    name: 'ContactUsForm',
    components: {
      TwoColumns,
      Accordion
    },
    directives: {
      tag
    },
    mixins: [recaptcha],
    props: {
      personalDataTitle: { type: String, required: false, default: 'About you' },
      requiredFieldText: { type: String, required: false, default: 'Required *' },
      firstNamePlaceholder: { type: String, required: false, default: 'First Name' },
      firstNameRequired: { type: Boolean, required: false, default: false },
      firstNameRequiredErrorMessage: { type: String, required: false, default: 'First Name Field Error' },
      lastNamePlaceholder: { type: String, required: false, default: 'Last Name' },
      lastNameRequired: { type: Boolean, required: false, default: false },
      lastNameRequiredErrorMessage: { type: String, required: false, default: 'Last Name Field Error' },
      emailPlaceholder: { type: String, required: true },
      emailRequired: { type: Boolean, required: false, default: true },
      emailRequiredErrorMessage: { type: String, required: false, default: 'Email is required' },
      emailFormatErrorMessage: { type: String, required: false, default: 'Enter a valid email' },
      confirmEmailPlaceholder: { type: String, required: false, default: 'Confirm Email' },
      confirmEmailErrorMessage: { type: String, required: false, default: 'Confirm email' },
      locationTitle: { type: String, required: false, default: 'Add my mailing address' },
      locationDescription: { type: String, required: false, default: 'Location description' },
      streetPlaceholder: { type: String, required: false, default: 'Street Address' },
      street2Placeholder: { type: String, required: false, default: 'Street Address' },
      cityPlaceholder: { type: String, required: false, default: 'City' },
      stateSelectList: { type: Array, required: true },
      statePlaceholder: { type: String, required: false, default: 'State' },
      zipCodePattern: { type: String, default: '[0-9]{5}' },
      zipcodePlaceholder: { type: String, required: false, default: 'Zip Code' },
      zipcodeErrorMessage: { type: String, required: false, default: 'Zipcode needs to have 5 digits' },
      phonePlaceholder: { type: String, required: false, default: 'Phone Number' },
      phoneErrorMessage: { type: String, required: false, default: 'Phone needs to have 10 digits' },
      messageTitle: { type: String, required: false, default: 'Your Message' },
      messagePlaceholder: { type: String, required: false, default: 'Message' },
      messageRequired: { type: Boolean, required: false, default: true },
      messageCharacterCount: { type: Number, required: false, default: 0 },
      messageErrorMessage: { type: String, required: false, default: 'Type in a message' },
      apiUrl: { type: String, required: true },
      submitBtnLabel: { type: String, required: true },
      recaptcha: { type: Object, required: false, default: undefined },
      disclaimerText: { type: String, required: false, default: 'Disclaimer' },
      disclaimerErrorMessage: { type: String, required: false, default: 'Disclaimer error' },
      eventLabel: { type: String, required: false, default: 'Contact Us Form' },
      expandedAddress: { type: Boolean, required: false, default: true },
      optionGroups: { type: Array, required: true },
      productData: { type: Object, required: true },
      faq: { type: Object, required: true },
      selectTopicLabel: { type: String, required: false, default: 'Select a topic' },
      productRequiredErrorMessage: { type: String, required: false, default: 'Select a product' },
      displayDisclaimer: {type: Boolean, required: false, default: true}
    },
    data() {
      return {
        emailAddress: '',
        confirmEmailAddress: '',
        confirmEmailDirty: false,
        emailDirty: false,
        serverError: false,
        serverErrorMessage: '',
        success: false,
        successMessage: '',
        recaptchaError: false,
        displayingAddress: true,
        firstName: '',
        firstNameDirty: false,
        lastName: '',
        lastNameDirty: false,
        street: '',
        street2: '',
        city: '',
        state: '',
        zipcode: '',
        zipcodeDirty: false,
        phone: '',
        phoneDirty: false,
        message: '',
        messageDirty: false,
        displayFormSuccess: false,
        disclaimerChecked: false,
        disclaimerError: false,
        selectedTopic: '',
        selectedProduct: '',
        selectedProductSize: '',
        selectedProductPurchase: '',
        selectedProductCode: '',
        selectProductDirty: false,
        displayCodeTooltip: false
      };
    },
    computed: {
      requiredError() {
        return this.emailAddress === '' && this.emailDirty;
      },
      invalidError() {
        return !regex.emailAddressRegex.test(this.emailAddress) && this.emailDirty;
      },
      showSuccessMessage() {
        return !this.requiredError && !this.invalidError && !this.serverError && this.success && !this.$slots['success-modal'];
      },
      successModal() {
        return !this.requiredError && !this.invalidError && !this.serverError && this.success && this.$slots['success-modal'] && this.$slots['success-modal'].length > 0;
      },
      firstNameError() {
        return this.firstNameRequired && this.firstNameDirty && !this.firstName.length;
      },
      lastNameError() {
        return this.lastNameRequired && this.lastNameDirty && !this.lastName.length;
      },
      confirmEmailError() {
        return this.confirmEmailDirty && this.confirmEmailAddress !== this.emailAddress;
      },
      messageError() {
        return this.messageRequired && this.messageDirty && !this.message.length;
      },
      submitTagging() {
        return {
          event: 'uaevent',
          ecommerce: 'undefined',
          eventCategory: 'contact form',
          eventAction: 'validate::form',
          eventLabel: this.eventLabel,
          event_name: 'submit_contact_form',
          form_name: this.eventLabel,
          requestType: this.selectedTopic.label,
        };
      },
      zipcodeRequired() {
        return this.zipcode.length > 0 && this.displayingAddress;
      },
      phoneRequired() {
        return this.phone.length > 0 && this.displayingAddress;
      },
      zipcodeError() {
        const requiredLength = Number(this.zipCodePattern.match(/\{(.*?)\}/)[1]);
        const hasInvalidLength = this.zipcode.length !== requiredLength;

        return this.zipcodeRequired && this.zipcodeDirty && hasInvalidLength;
      },
      phoneError() {
        return this.phoneRequired && this.phoneDirty && this.phone.length !== 10;
      },
      productSection() {
        return this.selectedTopic ? this.selectedTopic.formSections.includes('product') : false;
      },
      personalSection() {
        return this.selectedTopic ? this.selectedTopic.formSections.includes('personal') : false;
      },
      locationSection() {
        return this.selectedTopic ? this.selectedTopic.formSections.includes('location') : false;
      },
      messageSection() {
        return this.selectedTopic ? this.selectedTopic.formSections.includes('message') : false;
      },
      selectedFaq() {
        return this.selectedTopic ? this.selectedTopic.faq : [];
      },
      selectProductError() {
        return this.selectedProduct === '' && this.selectProductDirty;
      },
      selectedProductSizes() {
        return this.selectedProduct ? this.selectedProduct.variants : [];
      },
      selectedProductHasSizes() {
        return this.selectedProduct ? this.selectedProduct.variants.length > 0 : false;
      }
    },
    watch: {
      emailAddress(newVal) {
        if (!newVal) {
          this.emailAddress = '';
          this.emailDirty = false;
          this.serverError = false;
          this.serverErrorMessage = '';
          this.success = false;
          this.successMessage = '';
        }
      },
      successModal() {
        if (this.successModal) {
          this.openSuccessModal();
        }
      }
    },
    mounted() {
      this.displayingAddress = this.expandedAddress;

      if (this.recaptcha) {
        this.loadRecaptchaOnInput(this.$refs.form, this.recaptcha.siteKey);
      }
    },
    methods: {
      async submit() {
        this.emailDirty = true;
        this.confirmEmailDirty = true;
        this.firstNameDirty = true;
        this.lastNameDirty = true;
        this.messageDirty = true;
        this.selectProductDirty = true;
        if (this.displayDisclaimer && !this.disclaimerChecked) {
          this.disclaimerError = true;
        }
        let requestBody = {
          topic: this.selectedTopic.label
        };

        if (this.productSection) {
          if (this.selectProductError) {
            this.$refs.selectProduct.focus();
            return;
          }
          requestBody = { ...requestBody, ...{
            productId: this.selectedProduct.id,
            productVariant: this.selectedProductSize,
            productPurchaseInfo: this.selectedProductPurchase,
            productCode: this.selectedProductCode
          } };
        }

        if (this.personalSection) {
          if (this.firstNameError) {
            this.$refs.firstName.focus();
            return;
          }

          if (this.lastNameError) {
            this.$refs.lastName.focus();
            return;
          }

          if (this.requiredError || this.invalidError) {
            this.$refs.emailField.focus();
            return;
          }

          if (this.confirmEmailError) {
            this.$refs.confirmEmail.focus();
            return;
          }
          requestBody = { ...requestBody, ...{
            firstName: this.firstName,
            lastName: this.lastName,
            emailAddress: this.emailAddress
          } };
        }

        if (this.locationSection) {
          if (this.zipcodeError) {
            this.$refs.zipcode.focus();
            return;
          }
          if (this.phoneError) {
            this.$refs.phone.focus();
            return;
          }
          requestBody = { ...requestBody, ...{
            street: this.street,
            street2: this.street2,
            city: this.city,
            state: this.state,
            zipcode: this.zipcode,
            phone: this.phone
          } };
        }

        if (this.messageSection) {
          if (this.messageError) {
            this.$refs.message.focus();
            return;
          }
          requestBody = { ...requestBody, ...{
            message: this.message
          } };
        }

        if (this.displayDisclaimer && !this.disclaimerChecked) {
          this.$refs.disclaimer.focus();
          return;
        }

        if (this.recaptcha) {
          requestBody.recaptchaToken = await this.generateToken(this.recaptcha.siteKey);
        }

        fetch(this.apiUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json;charset=utf-8' },
          credentials: 'same-origin',
          body: JSON.stringify(requestBody)
        })
          .then(this.parseJSON)
          .then(this.handleApiResponse)
          .catch(error => {
            console.error('Could not load answer', error); // eslint-disable-line no-console
          });
      },
      handleApiResponse(data) {
        if (this.recaptcha && !data.recaptchaSuccess) {
          this.recaptchaError = true;
          return;
        }
        if (data.error) {
          this.serverError = true;
          this.serverErrorMessage = data.errorMessage;
          return;
        } else {
          this.displayFormSuccess = true;
          this.success = true;
          this.sendTaggingEvent();
          this.$nextTick(() => {
            smoothScroll(this.$refs.formsuccess);
          });
        }
      },
      sendTaggingEvent() {
        AnalyticsHandler.pushDataLayer(this.submitTagging);
      },
      dirtyEmail() {
        this.serverError = false;
        this.emailDirty = true;
      },
      dirtyConfirmEmail() {
        this.confirmEmailDirty = true;
      },
      dirtyFirstName() {
        this.firstNameDirty = true;
      },
      dirtyLastName() {
        this.lastNameDirty = true;
      },
      dirtyMessage() {
        this.messageDirty = true;
      },
      dirtyZipcode() {
        this.zipcodeDirty = true;
      },
      dirtyPhone() {
        this.phoneDirty = true;
      },
      parseJSON(response) {
        return response.json();
      },
      toggleAddressDisplay() {
        this.displayingAddress = !this.displayingAddress;
      },
      isNumber: function(evt) {
        evt = (evt) ? evt : window.event;
        var charCode = (evt.which) ? evt.which : evt.keyCode;
        if ((charCode > 31 && (charCode < 48 || charCode > 57))) {
          evt.preventDefault();
        } else {
          return true;
        }
      },
      showCodeTooltip() {
        this.displayCodeTooltip = true;
      },
      validateZipCodeKeypress(evt) {
        const allowedCharacters = this.zipCodePattern.match(/\[(.*?)\]/)[1];
        const regexp = new RegExp(`[${allowedCharacters}]`, 'i');

        return evt.key.match(regexp) || evt.preventDefault();
      },
      hideCodeTooltip() {
        this.displayCodeTooltip = false;
      }
    }
  };
</script>
